 
import { format, isSameDay, parseISO, startOfDay, subDays } from "date-fns";
import { sv } from "date-fns/locale";
import {
  CalendarEvent,
  CalendarEventWithLocalTime,
} from "../../store/ms-services/calendarApi";
import {
  WorkItemList,
  WorkItemStateEnum,
  WorkItemTypeEnum,
} from "../../store/services/timelineApiRTK";
import {
  getWorkItemColor,
  getWorkItemTypeName,
} from "../../utils/workItemUtils";

// En gemensam typ för alla kalenderhändelser
export interface CalendarItem {
  id: string;
  title: string;
  start: Date;
  end: Date;
  isAllDay?: boolean;
  type: "calendar" | "workItem" | "visit" | "merged";
  status?: string;
  color?: string;
  location?: string;
  workItemType?: WorkItemTypeEnum;
  relatedItems?: {
    workItem?: WorkItemList;
    calendarEvent?: CalendarEvent;
  };
}

// Konvertera Microsoft Calendar händelser till vårt gemensamma format
// Microsoft Outlook returnerar datum i UTC format, så vi konverterar dem till lokal tid
export const mapCalendarEvents = (
  events: CalendarEventWithLocalTime[]
): CalendarItem[] => {
  return events.map((event) => {
    // Konvertera UTC-datumen till lokal tid
    const start = new Date(event.localStartTime); // 'Z' indikerar att det är UTC
    const end = new Date(event.localEndTime);

    return {
      id: event.id,
      title: event.subject,
      start,
      end,
      isAllDay: event.isAllDay ?? false,
      type: "calendar",
      location: event.location?.displayName,
      color: "#2196f3", // Blå för kalenderhändelser
      relatedItems: { calendarEvent: event },
    };
  });
};

// Konvertera WorkItems till vårt gemensamma format med förbättrad styling
export const mapWorkItems = (workItems: WorkItemList[]): CalendarItem[] => {
  return workItems.map((item) => {
    // Använd scheduledTo som start om det finns, annars completeBefore
    // Alla datum från API:er antas vara ISO-strängar i lokal tid
    let startDate: Date;

    if (item.ScheduledTo) {
      startDate = parseISO(item.ScheduledTo);
    } else if (item.CompleteBefore) {
      startDate = parseISO(item.CompleteBefore);
    } else {
      startDate = new Date();
    }

    // För arbetsuppgifter, sätt varaktighet till 1 timme om bara ett datum finns
    const endDate = new Date(startDate);
    endDate.setHours(endDate.getHours() + 1);

    // Använd utility-funktioner för att få färg och namn baserat på type
    const itemType = item.Type as WorkItemTypeEnum;
    const typeColor = getWorkItemColor(itemType);
    const typeName = getWorkItemTypeName(itemType);

    return {
      id: `workitem-${item.Id}`,
      title: item.Name || typeName, // Använd typeName som fallback om namn saknas
      start: startDate,
      end: endDate,
      type: "workItem",
      status: WorkItemStateToString(item.State),
      color: typeColor,
      location: item.Customer?.Name || "",
      workItemType: itemType,
      relatedItems: {
        workItem: item,
      },
    };
  });
};

// Avgör om en händelse ska visas på en viss dag.
// Heldagshändelser spänner över alla sina dagar (exklusivt slutdatum),
// tidsatta händelser visas som tidigare bara på sin startdag.
export const eventOccursOnDay = (item: CalendarItem, day: Date): boolean => {
  if (item.isAllDay) {
    const dayStart = startOfDay(day).getTime();
    return item.start.getTime() <= dayStart && dayStart < item.end.getTime();
  }
  return isSameDay(item.start, day);
};

// Formatera tidsintervall för popovers, med heldagsvariant
export const formatItemTimeRange = (item: CalendarItem): string => {
  if (item.isAllDay) {
    // Slutdatumet är exklusivt — sista faktiska dagen är dagen innan
    const lastDay = subDays(item.end, 1);
    if (isSameDay(item.start, lastDay)) {
      return `${format(item.start, "EEEE d MMMM", { locale: sv })}, heldag`;
    }
    return `${format(item.start, "d MMMM", { locale: sv })} – ${format(
      lastDay,
      "d MMMM",
      { locale: sv }
    )}, heldag`;
  }
  return `${format(item.start, "EEEE d MMMM, HH:mm", { locale: sv })} - ${format(
    item.end,
    "HH:mm"
  )}`;
};

// Merge relaterade kalender- och workitems
export const mergeRelatedItems = (
  calendarEvents: CalendarItem[],
  workItems: CalendarItem[]
): CalendarItem[] => {
  const result: CalendarItem[] = [];
  const processedWorkItemIds = new Set<string>();
  // Gå igenom alla kalenderhändelser
  calendarEvents.forEach((calEvent) => {
    // Leta efter matchande workItems baserat på ID
    const relatedWorkItems = workItems.filter((workItem) => {
      // Kontrollera om kalender-ID matchar workItem.ExternalId

      if (workItem.relatedItems?.workItem?.ExternalId === calEvent.id) {
        processedWorkItemIds.add(workItem.id);
        return true;
      }
      return false;
    });

    if (relatedWorkItems.length > 0) {
      // Skapa en merged-händelse
      const firstWorkItem = relatedWorkItems[0];

      result.push({
        ...calEvent,
        type: "merged",
        workItemType: firstWorkItem.workItemType,
        color: firstWorkItem.color, // Använd workItem färg för merged-händelse
        status: firstWorkItem.status,
        relatedItems: {
          calendarEvent: calEvent.relatedItems?.calendarEvent,
          workItem: firstWorkItem.relatedItems?.workItem,
        },
      });
    } else {
      // Ingen matchning, behåll kalenderhändelse som den är
      result.push(calEvent);
    }
  });

  // Lägg till alla workItems som inte har matchats
  workItems.forEach((workItem) => {
    if (!processedWorkItemIds.has(workItem.id)) {
      result.push(workItem);
    }
  });

  return result;
};

// Hjälpfunktion för att formatera WorkItem-status
export const WorkItemStateToString = (state: number): string => {
  switch (state) {
    case WorkItemStateEnum.NotSet:
      return "Ej satt";
    case WorkItemStateEnum.Active:
      return "Aktiv";
    case WorkItemStateEnum.Closed:
      return "Avslutad";
    default:
      return "Okänd";
  }
};
